import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, Images } from "lucide-react";
import api from "@/lib/api";
import { PageHero } from "@/components/Shared";
import { useSiteContent } from "@/lib/siteContent";

const OTHER = "More Moments";
const src = (p) => (p ? (p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}${p}`) : "");

function PhotoGrid({ items }) {
  return (
    <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
      {items.map((g, i) => (
        <div key={g.id} className="relative group break-inside-avoid overflow-hidden border border-white/10 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`gallery-item-${g.id}`}>
          <img src={src(g.image)} alt={g.caption} className="w-full object-cover group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
            <span className="text-white text-sm font-medium">{g.caption}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function AlbumCard({ name, photos, onOpen, i }) {
  return (
    <button onClick={onOpen} className="group text-left bg-navy-paper border border-slate-800 hover:border-gold/60 transition-colors overflow-hidden animate-fade-up" style={{ animationDelay: `${i * 70}ms` }} data-testid={`gallery-album-${name}`}>
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={src(photos[0].image)} alt={name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/20 to-transparent" />
        <span className="absolute top-3 right-3 bg-navy/80 text-gold text-xs px-2 py-1 flex items-center gap-1"><Images className="w-3.5 h-3.5" /> {photos.length}</span>
      </div>
      <div className="p-4">
        <div className="font-display text-lg font-bold text-white group-hover:text-gold transition-colors">{name}</div>
        <div className="text-slate-500 text-xs uppercase tracking-widest mt-1">View album</div>
      </div>
    </button>
  );
}

export default function Gallery() {
  const [items, setItems] = useState([]);
  const content = useSiteContent();
  const hero = content?.gallery?.hero_image || "";
  const [params, setParams] = useSearchParams();
  const open = params.get("album");
  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const albums = {};
  items.forEach((g) => { const a = g.album || OTHER; (albums[a] = albums[a] || []).push(g); });
  const names = Object.keys(albums).sort((a, b) => (a === OTHER) - (b === OTHER));
  const onlyOther = names.length === 1 && names[0] === OTHER;
  const select = (a) => { setParams(a ? { album: a } : {}); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <div data-testid="gallery-page">
      <PageHero
        title={open || "Gallery"}
        subtitle={open ? `${(albums[open] || []).length} photos` : "Moments from the felt — final tables, big wins, and the faces of the Legends community."}
        image={content ? (src(hero) || "https://images.pexels.com/photos/7005504/pexels-photo-7005504.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1600") : ""}
        testId="gallery-hero"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        {open ? (
          <>
            <button onClick={() => select(null)} className="mb-8 inline-flex items-center gap-2 text-gold text-xs uppercase tracking-widest hover:text-gold-light" data-testid="gallery-back-btn"><ArrowLeft className="w-4 h-4" /> All albums</button>
            <PhotoGrid items={albums[open] || []} />
            {items.length > 0 && !albums[open] && <p className="text-slate-400 text-center py-20" data-testid="gallery-album-empty">This album has no photos yet.</p>}
          </>
        ) : onlyOther ? (
          <PhotoGrid items={items} />
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-albums">
            {names.map((a, i) => <AlbumCard key={a} name={a} photos={albums[a]} i={i} onOpen={() => select(a)} />)}
          </div>
        )}
        {items.length === 0 && <p className="text-slate-400 text-center py-20">No photos yet.</p>}
      </div>
    </div>
  );
}
